import { Button, Card, Muted, Stack, color, space, status } from '@biopeak/ui';
import { useState } from 'react';
import {
  useLabResults,
  useRevalidateDocument,
  useReviewDocument,
  type Decisao,
  type LabResultComNome,
} from '../lib/documents.ts';

/**
 * O PORTÃO: onde o valor lido por modelo passa a valer clinicamente.
 *
 * TRÊS REGRAS DE INTERFACE QUE SUSTENTAM O PORTÃO
 *
 * 1. **O trecho de origem fica ao lado do valor, sempre.** O médico valida
 *    contra a FONTE, não contra a nossa transcrição. Sem isso a revisão vira
 *    carimbo, e um portão que carimba não é portão.
 *
 * 2. **"Confirmar todos" existe, mas não alcança tudo.** Vinte e cinco analitos
 *    revisados um a um viram tortura, e tortura vira clique automático — o
 *    oposto do que queremos. Então o lote existe para o caso comum, e as linhas
 *    suspeitas (sem ancoragem ou fora da faixa plausível) ficam DE FORA dele,
 *    exigindo decisão individual.
 *
 * 3. **Suspeito é visualmente distinto e diz POR QUÊ.** "Não encontrado no
 *    documento" e "fora da faixa possível" são problemas diferentes e pedem
 *    olhares diferentes.
 *
 * ACHADO EM CAMPO: um hemograma completo real trouxe ~95 analitos. Sem
 * colapso, buscar ou paginar, isso é uma rolagem só, repetida em CADA
 * documento do paciente — e um exame já revisado por inteiro continua
 * ocupando a tela inteira para sempre. As três mitigações abaixo atacam
 * exatamente isso: o painel some sozinho quando não há mais nada pendente
 * (`aberto` default segue `pendentes.length > 0`, não uma escolha do médico
 * até ele mexer), a busca filtra por nome sem exigir acento, e só a fila sem
 * urgência (confiável + já revisado) pagina — o que exige decisão individual
 * (`suspeitos`) nunca fica escondido atrás de "mostrar mais".
 */
const LIMITE_INICIAL = 15;
const INCREMENTO_PAGINA = 15;

/** Sem acento e minúsculo, para "hba1c" achar "HbA1c" e "hemoglobina" achar "Hemoglobina". */
function normalizarBusca(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

function bateNaBusca(r: LabResultComNome, busca: string): boolean {
  if (!busca) return true;
  const alvo = normalizarBusca(busca);
  return (
    normalizarBusca(r.analyte?.display_name ?? '').includes(alvo)
    || normalizarBusca(r.analyte_key).includes(alvo)
  );
}

export function DocumentReview({ documentId }: { documentId: string }) {
  const resultados = useLabResults(documentId);
  const revisar = useReviewDocument(documentId);
  const revalidar = useRevalidateDocument(documentId);
  const [correcoes, setCorrecoes] = useState<Record<string, string>>({});
  // `null` = "segue o automático" (aberto enquanto houver pendente, fechado
  // quando não houver). Só vira booleano fixo depois que o médico clica —
  // a partir daí a escolha dele manda, mesmo que o status mude por baixo.
  const [abertoManual, setAbertoManual] = useState<boolean | null>(null);
  const [busca, setBusca] = useState('');
  const [visiveis, setVisiveis] = useState(LIMITE_INICIAL);

  if (resultados.isLoading) return <Muted>Carregando leitura…</Muted>;

  const linhas = resultados.data ?? [];
  const pendentes = linhas.filter((r) => r.status === 'pendente');

  if (linhas.length === 0) return null;

  const confiaveis = pendentes.filter((r) => r.anchored && r.plausible);
  const suspeitos = pendentes.filter((r) => !r.anchored || !r.plausible);
  const aberto = abertoManual ?? pendentes.length > 0;

  function decidir(id: string, acao: Decisao['acao']) {
    const valor = correcoes[id];
    revisar.mutate([
      acao === 'corrigir' && valor ? { id, acao, valor } : { id, acao },
    ]);
  }

  // Suspeito nunca pagina: são poucos e cada um exige decisão individual,
  // escondê-los atrás de "mostrar mais" seria o oposto do que o portão pede.
  const suspeitosFiltrados = suspeitos.filter((r) => bateNaBusca(r, busca));
  const restante = [...confiaveis, ...linhas.filter((r) => r.status !== 'pendente')]
    .filter((r) => bateNaBusca(r, busca));
  const restanteVisivel = restante.slice(0, visiveis);
  const temMais = restante.length > restanteVisivel.length;

  return (
    <Stack gap={space.md}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: space.sm,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => setAbertoManual(!aberto)}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: space.xs,
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span style={{ fontSize: '12px', color: color.textSecondary }}>
            {aberto ? '▾' : '▸'}
          </span>
          <Stack gap="2px">
            <span style={{ fontSize: '15px', fontWeight: 600 }}>
              Valores lidos automaticamente
            </span>
            <Muted style={{ fontSize: '12px' }}>
              {pendentes.length > 0
                ? `${pendentes.length} aguardando sua conferência · nenhum entra em gráfico ou alerta antes disso`
                : `Tudo conferido · ${linhas.length} valores`}
            </Muted>
          </Stack>
        </button>

        {/* "Aprovar todos" pedido pelo médico — mas o nome não pode prometer
            mais do que a regra 2 do cabeçalho permite: suspeitos (sem
            ancoragem ou fora da faixa) SEMPRE ficam de fora do lote, mesmo
            clicando aqui. Por isso o rótulo já entrega o número exato do que
            o clique faz, em vez de um "Aprovar tudo" que um médico apressado
            leria como "os N pendentes inteiros". */}
        {aberto && (
          <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
            {/* O "fiscal": roda a MESMA extração mais duas vezes e grava
                quantas das leituras bateram por analito — não muda nenhum
                valor, só dá ao médico um dado a mais pra decidir. Fica
                disponível mesmo sem pendente, porque revalidar um exame já
                confirmado também é uma decisão legítima (ex.: dúvida
                levantada depois). */}
            <Button
              variant="secondary"
              onClick={() => revalidar.mutate()}
              disabled={revalidar.isPending}
            >
              {revalidar.isPending ? 'Revalidando…' : 'Revalidar com IA (mais 2 leituras)'}
            </Button>

            {confiaveis.length > 0 && (
              <Button
                onClick={() =>
                  revisar.mutate(
                    confiaveis.map((r) => ({ id: r.id, acao: 'confirmar' as const })),
                  )
                }
                disabled={revisar.isPending}
              >
                {revisar.isPending
                  ? 'Salvando…'
                  : `Aprovar todos os confiáveis (${confiaveis.length})`}
              </Button>
            )}
          </div>
        )}
      </div>

      {revalidar.isError && (
        <Muted style={{ fontSize: '13px', color: status.red.fg }}>
          {(revalidar.error as Error).message}
        </Muted>
      )}

      {revalidar.isSuccess && revalidar.data?.status === 'ok' && (
        <Muted style={{ fontSize: '13px' }}>
          Revalidação concluída — {revalidar.data.leituras_gravadas ?? 0} leitura(s) nova(s)
          comparada(s) contra os valores já lidos.
        </Muted>
      )}

      {aberto && (
        <>
          {suspeitos.length > 0 && (
            <div
              style={{
                background: status.yellow.bg,
                border: `1px solid ${status.yellow.border}`,
                borderRadius: '8px',
                padding: space.sm,
              }}
            >
              <span style={{ fontSize: '13px', color: status.yellow.fg }}>
                <strong>
                  {suspeitos.length}{' '}
                  {suspeitos.length === 1 ? 'valor exige' : 'valores exigem'} sua decisão
                  individual.
                </strong>{' '}
                Ficam fora do "confirmar sem ressalva" de propósito — confira cada um
                contra o exame original antes de aceitar.
              </span>
            </div>
          )}

          {linhas.length > LIMITE_INICIAL && (
            <input
              type="search"
              placeholder="Buscar analito…"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setVisiveis(LIMITE_INICIAL);
              }}
              style={{
                padding: `6px ${space.sm}`,
                border: `1px solid ${color.border}`,
                borderRadius: '8px',
                font: 'inherit',
                fontSize: '13px',
              }}
            />
          )}

          {busca && suspeitosFiltrados.length === 0 && restanteVisivel.length === 0 && (
            <Muted style={{ fontSize: '13px' }}>Nenhum analito bate com "{busca}".</Muted>
          )}

          <Stack gap={space.sm}>
            {[...suspeitosFiltrados, ...restanteVisivel].map((r) => (
              <LinhaDeResultado
                key={r.id}
                resultado={r}
                correcao={correcoes[r.id] ?? ''}
                onCorrecao={(v) => setCorrecoes((c) => ({ ...c, [r.id]: v }))}
                onDecidir={(acao) => decidir(r.id, acao)}
                ocupado={revisar.isPending}
              />
            ))}
          </Stack>

          {temMais && (
            <Button
              variant="secondary"
              onClick={() => setVisiveis((v) => v + INCREMENTO_PAGINA)}
            >
              {`Mostrar mais ${Math.min(INCREMENTO_PAGINA, restante.length - restanteVisivel.length)}`}
            </Button>
          )}

          {revisar.isError && (
            <Muted style={{ fontSize: '13px', color: status.red.fg }}>
              Não foi possível salvar. Tente de novo.
            </Muted>
          )}
        </>
      )}
    </Stack>
  );
}

/**
 * Selo de quantas leituras independentes da IA bateram com o valor gravado.
 *
 * NÃO é um selo de aprovação — o texto é descritivo, não um "ok" verde
 * genérico, de propósito: bater 3/3 reduz a chance de um erro isolado do
 * modelo, mas não substitui olhar o trecho de origem (as três leituras
 * podem errar igual se o PDF tiver uma coluna desalinhada, por exemplo).
 */
function SeloDeConcordancia({ bateram, total }: { bateram: number; total: number }) {
  const tudoBateu = bateram === total;
  const cor = tudoBateu ? status.green.fg : status.yellow.fg;

  return (
    <span style={{ fontSize: '12px', color: cor, fontWeight: 500 }}>
      {tudoBateu
        ? `✓ ${bateram}/${total} leituras da IA bateram`
        : `${bateram}/${total} leituras da IA bateram`}
    </span>
  );
}

function rotuloDeStatus(s: string): { texto: string; cor: string } {
  switch (s) {
    case 'confirmado': return { texto: 'Confirmado', cor: status.green.fg };
    case 'corrigido':  return { texto: 'Corrigido',  cor: status.green.fg };
    case 'rejeitado':  return { texto: 'Rejeitado',  cor: color.textSecondary };
    default:           return { texto: 'Pendente',   cor: status.yellow.fg };
  }
}

function LinhaDeResultado({
  resultado: r,
  correcao,
  onCorrecao,
  onDecidir,
  ocupado,
}: {
  resultado: LabResultComNome;
  correcao: string;
  onCorrecao: (v: string) => void;
  onDecidir: (acao: Decisao['acao']) => void;
  ocupado: boolean;
}) {
  const suspeito = !r.anchored || !r.plausible;
  const pendente = r.status === 'pendente';
  const rotulo = rotuloDeStatus(r.status);

  return (
    <div
      style={{
        borderLeft: `3px solid ${
          pendente && suspeito ? status.yellow.border : color.border
        }`,
        background: pendente && suspeito ? status.yellow.bg : 'transparent',
        paddingLeft: space.sm,
        paddingTop: space.xs,
        paddingBottom: space.xs,
      }}
    >
      <Stack gap={space.xs}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: space.sm,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '15px', fontWeight: 600 }}>
            {r.analyte?.display_name ?? r.analyte_key}
          </span>
          <span style={{ fontSize: '15px', fontVariantNumeric: 'tabular-nums' }}>
            {r.value} {r.unit}
          </span>
          {(r.reference_low != null || r.reference_high != null) && (
            <Muted style={{ fontSize: '12px' }}>
              ref {r.reference_low ?? '–'}–{r.reference_high ?? '–'}
            </Muted>
          )}
          <span style={{ fontSize: '12px', color: rotulo.cor, fontWeight: 600 }}>
            {rotulo.texto}
          </span>
          {r.value_original != null && (
            <Muted style={{ fontSize: '12px' }}>
              (o modelo tinha lido {r.value_original})
            </Muted>
          )}
          {r.ai_agreement_total != null && <SeloDeConcordancia bateram={r.ai_agreement ?? 0} total={r.ai_agreement_total} />}
        </div>

        {/* A PROCEDÊNCIA. O médico confere contra isto, não contra o número
            acima — é a diferença entre revisar e carimbar. */}
        {r.source_snippet && (
          <div
            style={{
              fontSize: '12px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              color: color.textSecondary,
              background: color.surface,
              padding: `2px ${space.sm}`,
              borderRadius: '6px',
              overflowX: 'auto',
              whiteSpace: 'pre',
            }}
          >
            {r.page ? `p.${r.page}  ` : ''}
            {r.source_snippet}
          </div>
        )}

        {pendente && suspeito && (
          <span style={{ fontSize: '12px', color: status.yellow.fg }}>
            {!r.anchored && 'O trecho citado não foi encontrado no documento. '}
            {!r.plausible && 'Valor fora da faixa fisiologicamente possível, ou unidade não reconhecida. '}
          </span>
        )}

        {pendente && (
          <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={() => onDecidir('confirmar')} disabled={ocupado}>
              Confirmar
            </Button>
            <input
              type="number"
              step="any"
              inputMode="decimal"
              placeholder="corrigir para…"
              value={correcao}
              onChange={(e) => onCorrecao(e.target.value)}
              style={{
                width: '130px',
                padding: `4px ${space.sm}`,
                border: `1px solid ${color.border}`,
                borderRadius: '8px',
                font: 'inherit',
                fontSize: '13px',
              }}
            />
            <Button
              variant="secondary"
              onClick={() => onDecidir('corrigir')}
              disabled={ocupado || !correcao}
            >
              Salvar correção
            </Button>
            <button
              type="button"
              onClick={() => onDecidir('rejeitar')}
              disabled={ocupado}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                fontSize: '13px',
                color: color.textSecondary,
                textDecoration: 'underline',
                cursor: ocupado ? 'wait' : 'pointer',
              }}
            >
              Descartar
            </button>
          </div>
        )}
      </Stack>
    </div>
  );
}
